import { withoutDefaultEventBehavior } from '@assembly-web/services';
import { EllipsisHorizontalIcon } from '@heroicons/react/24/outline';
import {
  Content,
  Item,
  Menu,
  Portal,
  Root as MenuRoot,
  Trigger,
} from '@radix-ui/react-menubar';
import { Root, ToggleGroup, ToggleItem } from '@radix-ui/react-toolbar';
import type {
  ForwardRefExoticComponent,
  FunctionComponent,
  PropsWithoutRef,
  RefAttributes,
  SVGProps,
} from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { twMerge } from 'tailwind-merge';

import { Tooltip } from './Tooltip';

const messages = defineMessages({
  moreLabel: {
    defaultMessage: 'more',
    id: 'gk3x/E',
  },
});

export type ToolbarItem = {
  id: string;
  text: string;
  icon:
    | ForwardRefExoticComponent<
        PropsWithoutRef<SVGProps<SVGSVGElement>> & {
          title?: string;
          titleId?: string;
        } & RefAttributes<SVGSVGElement>
      >
    | FunctionComponent<
        SVGProps<SVGSVGElement> & { title?: string | undefined }
      >;
  disabled?: boolean;
  isAlertStyle?: boolean;
  tooltipText?: string;
};

export type ToolbarProps = {
  className?: string;
  hideBorder?: boolean;
  compact?: boolean;
  hideToolbar: boolean;
  onClosed?: () => void;
  primaryToolbarItems?: ToolbarItem[];
  secondaryToolbarItems: ToolbarItem[];
  onToggle?: (isOpen: boolean) => void;
  onMenuItemClick: (args: ToolbarItem) => void;
};

export function Toolbar({
  className,
  hideBorder = false,
  compact = false,
  onClosed,
  onToggle,
  hideToolbar,
  onMenuItemClick,
  primaryToolbarItems,
  secondaryToolbarItems,
}: ToolbarProps) {
  const { formatMessage } = useIntl();
  return (
    <Root
      className={twMerge(
        'flex h-8 w-full rounded-lg bg-gray-1',
        !hideBorder && 'border border-gray-5',
        hideToolbar ? 'invisible' : 'visible',
        primaryToolbarItems?.length === 0 ? 'p-[3px]' : 'p-[4px]',
        compact ? 'h-6 w-6 items-center justify-center hover:bg-gray-3' : 'h-8',
        className
      )}
      orientation="horizontal"
      onClick={withoutDefaultEventBehavior(() => {})}
    >
      <ToggleGroup className="flex" type="single">
        {primaryToolbarItems?.map((item) => (
          <ToggleItem
            key={item.id}
            value={item.text}
            aria-label={item.text}
            tabIndex={0}
            className="flex h-6 w-6 items-center justify-center rounded hover:bg-gray-3"
            onClick={withoutDefaultEventBehavior(() => onMenuItemClick(item))}
          >
            <Tooltip key={item.id} tooltipText={item.text}>
              <item.icon width="16px" height="16px" strokeWidth={2} />
            </Tooltip>
          </ToggleItem>
        ))}
        {secondaryToolbarItems.length > 0 && (
          <MenuRoot
            className="flex"
            onValueChange={(event) => {
              onToggle?.(Boolean(event));
            }}
          >
            <Menu>
              <Trigger
                className="relative inline-block text-left"
                aria-label="ellipsis"
              >
                <ToggleItem
                  asChild={true}
                  value={formatMessage(messages.moreLabel)}
                  title={formatMessage(messages.moreLabel)}
                  aria-label={formatMessage(messages.moreLabel)}
                  className="flex items-center justify-center rounded hover:bg-gray-3"
                  onClick={withoutDefaultEventBehavior(() => {})}
                >
                  <EllipsisHorizontalIcon
                    className={twMerge(
                      'fill-gray-10 stroke-2',
                      compact ? 'h-4 w-4' : 'h-6 w-6'
                    )}
                  />
                </ToggleItem>
              </Trigger>
              <Portal>
                <>
                  <div className="fixed bottom-0 left-0 right-0 z-[12] h-full w-full"></div>
                  <Content
                    align="end"
                    alignOffset={-2}
                    sideOffset={6}
                    side="bottom"
                    className={twMerge(
                      'z-[12] max-h-[32rem] w-max origin-top overflow-auto rounded-md border border-gray-5 bg-gray-1 py-1 shadow-lg-down ring-opacity-5 focus:outline-none',
                      hideToolbar ? 'invisible' : 'visible'
                    )}
                    onCloseAutoFocus={() => {
                      onClosed?.();
                    }}
                  >
                    {secondaryToolbarItems.map((action) => {
                      const item = (
                        <div className="bg-gray-0 flex w-full items-center px-3 py-[9px] text-sm text-gray-8 hover:bg-gray-3 focus:outline-none">
                          <action.icon
                            className={twMerge(
                              'h-4 w-4 stroke-2',
                              action.isAlertStyle && 'text-error-6',
                              action.disabled && 'text-gray-7'
                            )}
                          />
                          <div
                            className={twMerge(
                              'ml-2 line-clamp-1 text-left',
                              action.isAlertStyle && 'text-error-6',
                              action.disabled && 'text-gray-7'
                            )}
                          >
                            {action.text}
                          </div>
                        </div>
                      );
                      return (
                        <Item
                          key={action.id}
                          onClick={
                            action.disabled
                              ? () => {}
                              : withoutDefaultEventBehavior(() => {
                                  onMenuItemClick(action);
                                })
                          }
                          className={twMerge(
                            'cursor-pointer focus:bg-primary-2 focus:outline-none',
                            action.disabled && 'cursor-default bg-gray-2'
                          )}
                          disabled={action.disabled}
                        >
                          {action.tooltipText ? (
                            <Tooltip
                              tooltipText={action.tooltipText}
                              contentClassName="max-w-[232px]"
                              side="left"
                            >
                              {item}
                            </Tooltip>
                          ) : (
                            item
                          )}
                        </Item>
                      );
                    })}
                  </Content>
                </>
              </Portal>
            </Menu>
          </MenuRoot>
        )}
      </ToggleGroup>
    </Root>
  );
}
